// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — steering controllers used only by the balance/mechanism sims (not shipped into
// the live game, but real logic). Each returns a per-zone energy vector for the current
// substep, exactly the shape a real input source produces.
//
//  · Reactor    — reads the board and waves over any FIREFLY it has "seen" (spawned at
//                 least reactionMs ago), never over a wasp. Proves every board is
//                 completable and gives the reference "how good is a skilled run".
//  · GreedyAll  — waves over EVERYTHING alive, wasps included. The control for Sting: it
//                 gets stung, so selectivity is load-bearing (a negative gap when the
//                 avoid-the-wasp rule is disabled).
//  · Flailer    — waves at a random zone each substep, blind to the board. The control
//                 that shows reading the swarm is the game, not decoration.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import type { GameState } from './game';

export interface Controller {
  energy(s: GameState): number[];
}

export interface ReactorOpts {
  /** Only wave over a mote once it has been visible this long (ms). */
  reactionMs?: number;
  /** If false, also wave over wasps (the GreedyAll behaviour). */
  avoidWasps?: boolean;
}

export class ReactorBot implements Controller {
  private readonly reactionMs: number;
  private readonly avoidWasps: boolean;
  constructor(opts: ReactorOpts = {}) {
    this.reactionMs = opts.reactionMs ?? 110;
    this.avoidWasps = opts.avoidWasps ?? true;
  }
  energy(s: GameState): number[] {
    const out = new Array<number>(s.zones).fill(0);
    for (let z = 0; z < s.zones; z++) {
      const m = s.byZone[z];
      if (!m || m.state !== 'alive') continue;
      if (this.avoidWasps && m.kind === 'wasp') continue;
      if (s.clock - m.tSpawn >= this.reactionMs) out[z] = 1;
    }
    return out;
  }
}

/** Waves at one random zone each substep, blind — the load-bearing control. */
export class FlailerBot implements Controller {
  private readonly rng: Rng;
  constructor(seed: number) {
    this.rng = makeRng(`waft:flail:${seed >>> 0}`);
  }
  energy(s: GameState): number[] {
    const out = new Array<number>(s.zones).fill(0);
    out[Math.floor(this.rng() * s.zones)] = 1;
    return out;
  }
}
